import {Fragmenter, XMLExporter} from 'substance'

/**
 * IDF xml top level importer
 *
 * @extends XMLExporter
 */
class IdfExporter extends XMLExporter {
    /**
     * Export the NewsML. Merge the IDF (doc) handled by Substance with
     * the full newsML (newsItem)
     * dom handled by the Writer.
     *
     * @param  {object} doc      Substance controlled document
     * @param  {object} newsItem Full newsML dom document
     * @return {string}          NewsML xml string
     */
    exportDocument(doc, newsItem) {
        this.state.doc = doc // FIXME: Is this needed???

        const {api} = this.context
        return _cleanupNewsitem(
            _moveTeaser(
                api,
                _replaceGroupInNewsItem(this, newsItem, doc, 'body')
            )
        )
    }

    /**
     * This code is copied from DOMExporter.js in substance. This is because
     * removing the encodeXMLEntities() invocation fixed the double encoding
     * problem (WRIT-255).
     *
     * Relying on Substance annotated text fields in objects double encodes
     * html entities. Ie & turns into &amp;amp;
     *
     * @param {string} text The string that should have annotations added
     * @param {array} annotations Array of annotation objects for the text
     */
    _annotatedText(text, annotations) {
        var self = this

        var annotator = new Fragmenter()
        annotator.onText = function (context, text) {
            // context.children.push(encodeXMLEntities(text))
            context.children.push(text)
        }
        annotator.onEnter = function (fragment) {
            var anno = fragment.node
            return {
                annotation: anno,
                children: []
            }
        }
        annotator.onExit = function (fragment, context, parentContext) {
            var anno = context.annotation
            var converter = self.getNodeConverter(anno)
            if (!converter) {
                converter = self.getDefaultPropertyAnnotationConverter()
            }
            var el
            // Text annotations should not have the same start and end tag, a.k.a being an empty tag WRIT-433
            if (anno.start.equals(anno.end)) {
                el = ""
            } else {
                if (converter.tagName) {
                    el = this.$$(converter.tagName)
                } else {
                    el = this.$$('span')
                }
                el.attr(this.config.idAttribute, anno.id)
                el.append(context.children)
                if (converter.export) {
                    el = converter.export(anno, el, self) || el
                }
            }
            parentContext.children.push(el)
        }.bind(this)
        var wrapper = { children: [] }
        annotator.start(wrapper, text, annotations)
        return wrapper.children
    }
}


/**
 * Replace old IDF group of specific type with the new Substance handled document group
 *
 * @param {IdfExporter} exporter
 * @param {object} newsItem Full NewsItem DOM structure the api operates on
 * @param {object} doc      The substance document
 * @param {string} type     Type of group to update from substance doc
 */
function _replaceGroupInNewsItem(exporter, newsItem, doc, type ) {
    const idfEl = newsItem.querySelector('idf')
    const oldGroupEl = newsItem.querySelector(`idf group[type="${type}"]`)

    if (!idfEl || !oldGroupEl) {
        throw new Error(`IDF or IDF group <${type}> not found in Newsitem DOM structure while exporting`)
    }

    const newGroupEl = newsItem.createElementNS(idfEl.namespaceURI, 'group')
    newGroupEl.setAttribute('type', type)

    for (var node of exporter.convertContainer(doc.get(type))) {
        newGroupEl.appendChild(node.el)
    }

    idfEl.replaceChild(newGroupEl, oldGroupEl)

    return newsItem
}

/**
 * Move teaser from contentMeta section into the content.
 *
 * FIXME: This should be done in pre validation hook in the plugin!!!
 *
 * @param  {object} api Writer api object
 * @param  {object} newsItem Full NewsItem DOM structure the api operates on
 */
function _moveTeaser(api, newsItem) {
    const idfEl = newsItem.querySelector('idf')
    const teaserTypes = api.getConfigValue('se.infomaker.ximteaser', 'types', false)
    const hasMultiTeaserSupport = teaserTypes !== false

    if (hasMultiTeaserSupport) {
        const queryForMetaTypes = teaserTypes.map(({type}) => `contentMeta > metadata > object[type="${type}"]`).join(',')
        const teaserContainer = newsItem.querySelector('object[type="x-im/teasercontainer"]')
        const teaserMetaElements = newsItem.querySelectorAll(queryForMetaTypes)
        const metadata = newsItem.querySelector('contentMeta > metadata')

        // Extract x-im/*-teaser objects and move it to its correct position if it exists
        teaserMetaElements.forEach((teaserElement) => metadata.removeChild(teaserElement))

        // Add fresh teaser elements to metadata
        if(teaserContainer !== null) {
            [...teaserContainer.children].forEach((newTeaserElement) => metadata.appendChild(newTeaserElement))

            // Remove teasercontainer from xml
            teaserContainer.parentElement.removeChild(teaserContainer)
        }
    }
    else {
        // Backwards compatibility for teaser plugin

        // Extract x-im/teaser object and move it to its correct position if it exists
        const oldTeaser = newsItem.querySelector('contentMeta > metadata > object[type="x-im/teaser"]');
        const metadata = newsItem.querySelector('contentMeta > metadata');

        if (oldTeaser) {
            metadata.removeChild(oldTeaser);
        }

        const newTeaser = idfEl.querySelector('object[type="x-im/teaser"]');

        if (newTeaser) {
            newTeaser.parentElement.removeChild(newTeaser);
            metadata.appendChild(newTeaser);
        }
    }

    return newsItem
}

/**
 * Cleanup and serialize newsItem
 *
 * @param  {object} newsItem Newsitem dom structure
 * @return {string}          Serialized XML string
 */
function _cleanupNewsitem(newsItem) {
    try {
        return _removeEmptyNamespaces(
            _superSerializeToRealXML(newsItem)
        )
    }
    catch(err) {
        throw new Error(`Failed cleaning NS and/or control codes when exporting: ${err.message}`)
    }
}

/**
 * HACK to convert empty XML tags from <tag></tag> to <tag/>
 *
 * @param newsItem
 * @returns {string}
 * @private
 */
function _superSerializeToRealXML(newsItem) {
    const serializer = new XMLSerializer()
    const domParser = new DOMParser()

    const serializedToString = _removeControlCodes(
        serializer.serializeToString(newsItem.documentElement)
    )

    const domSerialized = domParser.parseFromString(serializedToString,'text/xml')

    return serializer.serializeToString(domSerialized)
}

/**
 * Removes for XML illegal control codes from text (range from 0x00 - 0x1F with the exception of
 * TAB, CR and LF). See http://www.w3.org/TR/xml/#charsets
 * @param {string} serializedXML Serialized xml to process
 * @return {string} Serialized xml without control codes
 * @private
 */
function _removeControlCodes(serializedXML) {
    if (serializedXML === undefined) {
        return serializedXML
    }

    const regex = new RegExp("[\x00-\x08\x0b\x0c\x0e-\x1f]|(&nbsp;{1})", "g")
    if (regex.exec(serializedXML) !== null) {
        console.warn("Removing illegal XML character in content")
        return serializedXML.replace(regex, "")
    }

    return serializedXML
}

/**
 * Removes empty xmlns attributes created because the writer and substance
 * dont' create elements with correct namespace.
 *
 * HACK: This should not be necessary when substance handle namespaces correctly
 *
 * @param {string} serializedXML Serialized xml
 * @return {string} Serialized xml without empty namespaces
 * @private
 */
function _removeEmptyNamespaces(serializedXML) {
    if (serializedXML === undefined) {
        return serializedXML
    }

    const regex = new RegExp(" xmlns=\"\"", "g")
    if (regex.exec(serializedXML) !== null) {
        console.warn("Removing illegal empty namespace in XML")
        return serializedXML.replace(regex, "")
    }

    return serializedXML
}



export {IdfExporter}
